import Cookies from "js-cookie";
import { ulid } from "ulid";

const VISITOR_ID_KEY = "visitor_id";
const SESSION_ID_KEY = "session_id";
const USER_ID_KEY = "user_id";

const VISITOR_EXPIRY_DAYS = 365;
const SESSION_EXPIRY_DAYS = 1 / 12; // 2 hours

const isBrowser = typeof window !== "undefined";

const cookieOptions: Cookies.CookieAttributes = {
  domain: isBrowser ? `.${process.env.GATSBY_DOMAIN_URL}` : undefined,
  path: "/",
  sameSite: "Lax",
  secure: true,
};

function getOrCreateVisitorId(): string | null {
  if (!isBrowser) return null;

  const existing = Cookies.get(VISITOR_ID_KEY);
  if (existing) return existing;

  const id = ulid();
  Cookies.set(VISITOR_ID_KEY, id, {
    ...cookieOptions,
    expires: VISITOR_EXPIRY_DAYS,
  });
  return id;
}

function getOrCreateSessionId(): string | null {
  if (!isBrowser) return null;

  const existing = Cookies.get(SESSION_ID_KEY);
  if (existing) {
    // Renew session on each interaction
    Cookies.set(SESSION_ID_KEY, existing, {
      ...cookieOptions,
      expires: SESSION_EXPIRY_DAYS,
    });
    return existing;
  }

  const id = ulid();
  Cookies.set(SESSION_ID_KEY, id, {
    ...cookieOptions,
    expires: SESSION_EXPIRY_DAYS,
  });
  return id;
}

function getUserId(): string | null {
  if (!isBrowser) return null;
  return Cookies.get(USER_ID_KEY) ?? null;
}

function setUserId(id: string): void {
  if (!isBrowser) return;
  Cookies.set(USER_ID_KEY, id, {
    ...cookieOptions,
    expires: VISITOR_EXPIRY_DAYS,
  });
}

interface Identity {
  visitor_id: string | null;
  session_id: string | null;
  user_id: string | null;
}

function getIdentity(): Identity {
  return {
    visitor_id: getOrCreateVisitorId(),
    session_id: getOrCreateSessionId(),
    user_id: getUserId(),
  };
}

export const IdentityManager = {
  getOrCreateVisitorId,
  getOrCreateSessionId,
  getUserId,
  setUserId,
  getIdentity,
};
